import {checkExists, exists} from 'external/dev_april_corgi+/js/common/asserts';
import {
  getFirstElement,
  getOnlyElement,
} from 'external/dev_april_corgi+/js/common/collections';

import {
  PipeId,
  NodeSpec,
  hashMaterial,
  subgraphIoPadId,
} from '../execution/graph';
import {PTransformer, PipeContext, Source} from '../execution/pipes';
import {DataType, DataValue, MaterialId, MaterialValue} from '../types/data';

export const RELABELED_TREATMENT = 'relabel';

export const NODE_SUBGRAPH_INPUT = {
  id: 'node_subgraph_input',
  label: 'Subgraph input',
  inputs: [
    {
      id: 'default',
      label: 'Default',
      type: {
        kind: 'any',
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      label: 'Value',
      type: {
        kind: 'any',
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const defaultPad = checkExists(node.inputs.get('default'));
    const outputPad = checkExists(node.outputs.get('value'));
    const types = new Set<DataType>();
    for (const link of outputPad.links.values()) {
      types.add(link.input.type);
    }
    const oldDefaultType = defaultPad.type;
    if (types.size === 0) {
      overlay.setType(defaultPad, {kind: 'any'});
      overlay.setType(outputPad, {kind: 'any'});
    } else if (types.size === 1) {
      overlay.setType(defaultPad, getOnlyElement(types));
      overlay.setType(outputPad, getOnlyElement(types));
    } else {
      const kinds = new Set([...types].map(t => t.kind));
      if (kinds.size === 1) {
        // TODO(april): we should do better but whatever
        const type = getFirstElement(types);
        overlay.setType(defaultPad, type);
        overlay.setType(outputPad, type);
      } else {
        overlay.addPipeError(
          overlay.nextIdFor(node),
          'Downstream nodes expect different kinds',
        );
        return;
      }
    }
    if (oldDefaultType.kind !== defaultPad.type.kind) {
      overlay.clearValue(defaultPad);
    }

    let inputs: Array<Source<DataValue>>;
    if (node.subgraphId) {
      const inputId = subgraphIoPadId(node.subgraphId, node.id, 'input');
      const inputPad = checkExists(node.inputs.get(inputId));
      // Propagating default values onto this pad when there isn't already a value
      if (!inputPad.unlinked && defaultPad.unlinked) {
        overlay.setValue(
          inputPad,
          defaultPad.unlinked.value,
          defaultPad.unlinked.constraints,
        );
      }
      inputs = overlay.padToSources(inputPad);
    } else {
      inputs = [];
    }
    const defaultSources = overlay.padToSources(defaultPad);
    const output = new PassthroughTransformer(
      overlay.nextIdFor(node),
      node.label,
      defaultSources,
      inputs,
    );
    overlay.setPadSource(outputPad, output);
  },
} as const satisfies NodeSpec;

export const NODE_SUBGRAPH_OUTPUT = {
  id: 'node_subgraph_output',
  label: 'Subgraph output',
  inputs: [
    {
      id: 'value',
      label: 'Value',
      type: {
        kind: 'any',
      },
    },
  ],
  outputs: [
    {
      id: 'default',
      label: 'Default',
      type: {
        kind: 'any',
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const inputPad = checkExists(node.inputs.get('value'));
    const defaultPad = checkExists(node.outputs.get('default'));
    const types = new Set<DataType>();
    for (const link of inputPad.links.values()) {
      types.add(link.output.type);
    }
    if (types.size === 0) {
      overlay.setType(inputPad, {kind: 'any'});
      overlay.setType(defaultPad, {kind: 'any'});
    } else if (types.size === 1) {
      overlay.setType(inputPad, getOnlyElement(types));
      overlay.setType(defaultPad, getOnlyElement(types));
    } else {
      const kinds = new Set([...types].map(t => t.kind));
      if (kinds.size === 1) {
        // TODO(april): we should do better but whatever
        const type = getFirstElement(types);
        overlay.setType(inputPad, type);
        overlay.setType(defaultPad, type);
      } else {
        overlay.addPadError(inputPad, 'Upstream nodes provide multiple kinds');
        return;
      }
    }

    const inputSources = overlay.padToSources(inputPad);
    const output = new PassthroughTransformer(
      overlay.nextIdFor(node),
      /* label= */ undefined,
      /* defaultSources= */ [],
      inputSources,
    );
    if (node.subgraphId) {
      const outputId = subgraphIoPadId(node.subgraphId, node.id, 'output');
      const outputPad = checkExists(node.outputs.get(outputId));
      // We need to ensure the default pad resolves or else the graph will hang, but we also don't
      // want to actually *do* anything. So we set up a transformer that sits between the inputs
      // and the defaults that resolves undefined. This doesn't guarantee that nothing will happen
      // but it's pretty close. And cases that tolerate undefined and still do something maybe just
      // really want to do something anyway.
      overlay.setPadSource(
        defaultPad,
        new UndefinedSource(overlay.nextIdFor(node), inputSources),
      );
      // Set the output so things outside of the subgraph can use it
      overlay.setPadSource(outputPad, output);
    } else {
      // Propagate onto the default pad so that the graph works when top-level
      overlay.setPadSource(defaultPad, output);
    }
  },
} as const satisfies NodeSpec;

class PassthroughTransformer extends PTransformer<DataValue, DataValue> {
  constructor(
    id: PipeId,
    private readonly label: string | undefined,
    private readonly defaultSources: Array<Source<DataValue>>,
    private readonly upstreams: Array<Source<DataValue>>,
  ) {
    super(id, [...defaultSources, ...upstreams]);
  }

  async collate(context: PipeContext): Promise<void> {
    const defaultSources = this.defaultSources.map(s =>
      checkExists(this.values.get(s)),
    );
    const defaultValues = defaultSources.map(s =>
      s.value ? this.maybeRelabel(s.value) : undefined,
    );
    const upstreams = this.upstreams.map(u => checkExists(this.values.get(u)));
    const upstreamValues = upstreams.map(u =>
      u.value ? this.maybeRelabel(u.value) : undefined,
    );

    // Inside the subgraph there is no inputs pad, so upstreamValues has length 0.
    // Outside the subgraph there is one pad and it gets an unlinked source that may be undefined
    // unless otherwise linked or specified.
    if (
      upstreamValues.length === 0 ||
      (upstreamValues.length === 1 &&
        getOnlyElement(upstreamValues) === undefined)
    ) {
      const promises = [];
      if (defaultSources.length === 0) {
        await this.gatherConstraints(undefined, context);
      } else if (defaultSources.length === 1) {
        const constraints = await this.gatherConstraints(
          defaultValues[0],
          context,
        );
        await defaultSources[0].resolve(constraints);
      } else {
        const constraints = await this.gatherConstraints(
          {kind: 'array', elements: defaultValues.filter(exists)},
          context,
        );
        const arrayed = constraints.array ?? [];
        let j = 0;
        for (let i = 0; i < defaultSources.length; ++i) {
          if (defaultSources[i].value && j < arrayed.length) {
            promises.push(defaultSources[i].resolve(arrayed[j]));
            j += 1;
          } else {
            promises.push(defaultSources[i].resolve({}));
          }
        }
      }
      promises.push(...upstreams.map(({resolve}) => resolve({})));
      await Promise.all(promises);
    } else if (upstreamValues.length === 1) {
      const constraints = await this.gatherConstraints(
        upstreamValues[0],
        context,
      );
      await Promise.all([
        ...defaultSources.map(s => s.resolve({})),
        upstreams[0].resolve(constraints),
      ]);
    } else {
      const constraints = await this.gatherConstraints(
        {kind: 'array', elements: upstreamValues.filter(exists)},
        context,
      );
      const arrayed = constraints.array ?? [];
      const promises = defaultSources.map(s => s.resolve({}));
      let j = 0;
      for (let i = 0; i < upstreams.length; ++i) {
        if (upstreams[i].value && j < arrayed.length) {
          promises.push(upstreams[i].resolve(arrayed[j]));
          j += 1;
        } else {
          promises.push(upstreams[i].resolve({}));
        }
      }
      await Promise.all(promises);
    }
  }

  // Suppose the default input is oAT_500 at 10mM, then downstream nodes will expect oAT_500 at
  // 10mM. But if the user then passes in oAS_15 at 10ng/ml all of that will be off. So we relabel
  // to 1x, ensuring units always work, and we relabel to the name of the node so that downstream
  // mix tables can reference regardless of input.
  private maybeRelabel<V extends DataValue>(value: V): V {
    if (!this.label) {
      return value;
    }

    if (value.kind === 'material') {
      return relabeledMaterial(value, this.label) as V;
    } else if (value.kind === 'plated_material') {
      return {...value, material: this.maybeRelabel(value.material)};
    } else {
      return value;
    }
  }
}

class UndefinedSource extends PTransformer<DataValue, DataValue> {
  constructor(id: PipeId, upstreams: Array<Source<DataValue>>) {
    super(id, upstreams);
  }

  protected async collate(context: PipeContext): Promise<void> {
    await this.gatherConstraints(undefined, context);
    await Promise.all(
      [...this.values.values()].map(({resolve}) => resolve({})),
    );
  }
}

function relabeledMaterial(
  material: MaterialValue,
  label: string,
): MaterialValue {
  const relabeled: MaterialValue = {
    kind: 'material',
    id: 'uncalculated' as unknown as MaterialId,
    label,
    amount: {kind: 'concentration', number: 1, unit: 'x'},
    contents: [material],
    tags: {treatment: RELABELED_TREATMENT},
  };
  relabeled.id = hashMaterial(relabeled);
  return relabeled;
}
